import { numberOfNights, getLocalDate } from '@alexis/helpers/date';
import React, { useState } from 'react';

import { clsx } from '@wego/alexis/helpers/clsx';
import { translateNumber } from '@wego/alexis/helpers/translation';
import { translateText } from '@wego/alexis/helpers/translation';

import Price from '@components/Price';

import { notUndefinedAndNull } from '@helpers/array';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { convertHotelRatePriceToPrice } from '@helpers/price';

import BedIcon from '@icons/bed.svg';
import ForkSpoonIcon from '@icons/fork_spoon.svg';
import ProhibitedIcon from '@icons/prohibited.svg';
import TickIcon from '@icons/tick.svg';
import Wifi from '@icons/wifi.svg';
import XCloseBigIcon from '@icons/x-close-big.svg';

import { Currency } from '@wegoTypes/currency';
import type {
  HotelBookingRoomTypesDataRoom,
  RoomTypesDataRoomBedType,
} from '@wegoTypes/hotelBooking/hotelRoomTypes';
import {
  HotelRateAmenity,
  HotelRateAmenityCode,
} from '@wegoTypes/hotels/metasearch/hotelRateAmenity';
import type { MetasearchHotelRate } from '@wegoTypes/hotels/metasearch/metasearchHotelRate';
import { Translations } from '@wegoTypes/translations';

import RoomAmenityIcon from '../RoomAmenityIcon';
import RoomImageGallery from '../RoomImageGallery';
import styles from './RoomDetail.module.scss';

interface RoomDetailProps {
  currency: Currency;
  exchangeRate: ExchangeRate;
  guestCount: number;
  locale: string;
  priceOptions: PriceOptions;
  rateAmenities: Array<HotelRateAmenity>;
  roomTypeWithRate: {
    rates: Array<MetasearchHotelRate>;
    roomType: HotelBookingRoomTypesDataRoom;
  };
  rateHandoffHref: (rate: MetasearchHotelRate) => string;
  selectedRateAmenityIds: Array<number>;
  translations: Translations;
}

const RoomDetail: React.FC<RoomDetailProps> = ({
  currency,
  exchangeRate,
  guestCount,
  locale,
  priceOptions,
  rateAmenities,
  rateHandoffHref,
  roomTypeWithRate,
  selectedRateAmenityIds,
  translations,
}) => {
  const [isOpenAmenities, setIsOpenAmenities] = useState<boolean>(false);

  const getBedTypeConfigurationDisplay = (
    bedTypeConfigurations: Array<RoomTypesDataRoomBedType>,
    locale: string,
  ): string => {
    const groupBedTypeConfigurationsByIndex: { [key: string]: string } =
      bedTypeConfigurations.reduce(
        (
          accumulator: { [key: string]: string },
          bedTypeConfigurations: RoomTypesDataRoomBedType,
        ) => {
          const stringIndex = bedTypeConfigurations.index.toString();

          if (!!accumulator[stringIndex]) {
            accumulator[stringIndex] += `, ${translateNumber(
              bedTypeConfigurations.quantity,
              locale === 'fa',
            )} ${bedTypeConfigurations.type}`;
          } else {
            accumulator[stringIndex] = `${translateNumber(
              bedTypeConfigurations.quantity,
              locale === 'fa',
            )} ${bedTypeConfigurations.type}`;
          }

          return accumulator;
        },
        {},
      );

    return Object.keys(groupBedTypeConfigurationsByIndex)
      .map((key) => groupBedTypeConfigurationsByIndex[key])
      .join(' / ');
  };

  const getRateAmenties = (
    rate: MetasearchHotelRate,
    rateAmenities: Array<HotelRateAmenity>,
  ): Array<HotelRateAmenity> => {
    return rate.rateAmenityIds
      .map((rateAmenityId) => rateAmenities.find((rateAmenity) => rateAmenity.id === rateAmenityId))
      .filter(notUndefinedAndNull)
      .sort((rateAmenityA, rateAmenityB) => {
        if (rateAmenityA.type === 'AMENITY' && rateAmenityB.type === 'PAYMENT') {
          return -1;
        }
        if (rateAmenityA.type === 'PAYMENT' && rateAmenityB.type === 'AMENITY') {
          return 1;
        }
        return 0;
      });
  };

  const getRateAmenitiesIcon = (rateAmenityCode: HotelRateAmenityCode) => {
    switch (rateAmenityCode) {
      case 'BREAKFAST_INCLUDED':
      case 'FULL_BOARD_MEAL':
      case 'HALF_BOARD_MEAL':
        return <ForkSpoonIcon />;
      case 'FREE_WIFI':
        return <Wifi />;
      case 'FREE_CANCELLATION':
        return <TickIcon className={clsx(styles.icon, styles.freeCancellation)} />;
      default:
        return <TickIcon />;
    }
  };

  const noFreeCancellationRateAmenity = (
    rateAmenityIds: Array<number>,
    rateAmenities: Array<HotelRateAmenity>,
  ): boolean => {
    return !rateAmenities.find(
      (rateAmenity) =>
        rateAmenity.code === 'FREE_CANCELLATION' && rateAmenityIds.includes(rateAmenity.id),
    );
  };

  const noMealRateAmenity = (
    rateAmenityIds: Array<number>,
    rateAmenities: Array<HotelRateAmenity>,
  ): boolean => {
    return !rateAmenities.find(
      (rateAmenity) =>
        ['BREAKFAST_INCLUDED', 'FULL_BOARD_MEAL', 'HALF_BOARD_MEAL', 'ALL_INCLUSIVE'].includes(
          rateAmenity.code,
        ) && rateAmenityIds.includes(rateAmenity.id),
    );
  };

  const nightCount = numberOfNights(
    getLocalDate(roomTypeWithRate.rates[0].checkIn).getTime(),
    getLocalDate(roomTypeWithRate.rates[0].checkOut).getTime(),
  );

  return (
    <div className={clsx(styles.container)}>
      <div className={styles.content}>
        <div className={styles.roomCard}>
          <RoomImageGallery
            roomImages={roomTypeWithRate.roomType.images.map((image) => image.url)}
            locale={locale}
          />

          <div>
            <div
              className={clsx(
                styles.roomTypeName,
                roomTypeWithRate.roomType.images.length > 0 && styles.marginTop,
              )}
            >
              {roomTypeWithRate.roomType.typeName}
            </div>

            {roomTypeWithRate.roomType.bedTypes.length ? (
              <div className={styles.bedType}>
                <BedIcon className={styles.bedIcon} />
                {getBedTypeConfigurationDisplay(roomTypeWithRate.roomType.bedTypes, locale)}
              </div>
            ) : null}

            {roomTypeWithRate.roomType.amenities.length ? (
              <div className={styles.amenities}>
                {roomTypeWithRate.roomType.amenities
                  .sort((amenityA, amenityB) => amenityA.priority - amenityB.priority)
                  .slice(0, isOpenAmenities ? roomTypeWithRate.roomType.amenities.length : 6)
                  .map((amenity) => {
                    return (
                      <div className={styles.amenity} key={amenity.id}>
                        <RoomAmenityIcon roomAmenityId={amenity.id} size={12} />
                        {amenity.nameAll[locale]}
                      </div>
                    );
                  })}
              </div>
            ) : null}

            {!isOpenAmenities && roomTypeWithRate.roomType.amenities.length > 5 ? (
              <div className={styles.viewMoreAmenities} onClick={() => setIsOpenAmenities(true)}>
                {translations.view_more_amenities}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.rates}>
          {roomTypeWithRate.rates.map((rate) => (
            <div className={styles.rate} key={rate.id}>
              <div className={styles.rateAmenity}>
                {noMealRateAmenity(rate.rateAmenityIds, rateAmenities) ? (
                  <div className={styles.rateAmenityLabel}>
                    <ProhibitedIcon />
                    {translations.no_meals_included}
                  </div>
                ) : null}

                {getRateAmenties(rate, rateAmenities).map((rateAmenity) => (
                  <div
                    key={rateAmenity.id}
                    className={clsx(
                      styles.rateAmenityLabel,
                      rateAmenity.code === 'FREE_CANCELLATION' && styles.freeCancellation,
                      selectedRateAmenityIds.includes(rateAmenity.id) && styles.selected,
                    )}
                  >
                    {getRateAmenitiesIcon(rateAmenity.code)}
                    {rateAmenity.name}
                  </div>
                ))}

                {noFreeCancellationRateAmenity(rate.rateAmenityIds, rateAmenities) ? (
                  <div className={clsx(styles.rateAmenityLabel, styles.policyNonRefundable)}>
                    <XCloseBigIcon />
                    {translations.uta_non_refundable}
                  </div>
                ) : null}

                {rateAmenities.find((rateAmenity) => rateAmenity.code === 'BUY_NOW_PAY_LATER') ? (
                  <div
                    className={clsx(
                      styles.rateAmenityLabel,
                      selectedRateAmenityIds.includes(10001) && styles.selected,
                    )}
                  >
                    <img
                      src={cloudinaryImageTransformations(
                        'https://assets.wego.com/image/upload/v1663418328/web/payment_cards/tabby_square_3x.png',
                        'c_fill',
                        'fl_lossy',
                        'q_auto:low',
                        'f_auto',
                        'w_48',
                        'h_48',
                      )}
                    />
                    {translations.lbl_interest_free_payments}
                  </div>
                ) : null}
              </div>

              <div className={styles.pricePerNightAndTaxes}>
                <Price
                  className={styles.price}
                  price={convertHotelRatePriceToPrice(
                    rate.price,
                    priceOptions.showTotalPrice,
                    priceOptions.isIncludingTaxOrFee,
                  )}
                  locale={locale}
                  currency={currency}
                  exchangeRate={exchangeRate}
                  isInline
                />
                <div className={styles.nightCountAndGuestCount}>
                  {translateText(
                    translations.night_count,
                    locale,
                    priceOptions.showTotalPrice ? nightCount : 1,
                  ) +
                    ', ' +
                    translateText(translations.guest_count, locale, guestCount)}
                </div>

                {priceOptions.isIncludingTaxOrFee ? (
                  <div className={styles.includedTaxAndFees}>{translations.incl_taxes_fees}</div>
                ) : null}
              </div>

              <a
                className={styles.bookNow}
                href={rateHandoffHref(rate)}
                rel='noreferrer'
                target='_blank'
              >
                {translations.book_now}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoomDetail;
